import { Button } from "$components/button/button";
import { IconButton } from "$components/button/icon-button";
import { Table, type TableColumnDefinition } from "$components/table";
import { randomId } from "$routes/_lib/random-id";
import RHFTextField from "$routes/price-list/_lib/components/rhf/text-field";
import { styled } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import React from "react";
import { useFieldArray } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import type { FormData } from "./use-form-schema";

interface CustomItemRow {
  id: string;
  name: string;
}

type ItemsTableProps = {
  customItems: CustomItemRow[];
  handleDelete: (index: number) => void;
  fieldArrayName: string;
} & Partial<React.ComponentProps<typeof Table>>;

const ItemsTable: FC<ItemsTableProps> = ({
  customItems,
  handleDelete,
  fieldArrayName,
  ...delegateProps
}) => {
  const { formatMessage } = useIntl();

  const columns: TableColumnDefinition[] = [
    {
      field: "name",
      flex: 6,
      headerName: formatMessage({
        id: "priceList.createCustomItems.column.name",
        defaultMessage: "Item",
      }),
      renderCell: (_, index) => {
        const name = `${fieldArrayName}.${index}.name`;
        return (
          <RHFTextField
            label="Name"
            name={name}
            inputProps={{ "data-testid": name }}
            fullWidth
          />
        );
      },
    },
    {
      field: "origin",
      flex: 2,
      headerName: formatMessage({
        id: "priceList.createCustomItems.column.origin",
        defaultMessage: "Type",
      }),
      renderCell: () => (
        <FormattedMessage
          id="priceList.createCustomItems.origin"
          defaultMessage="Custom"
        />
      ),
    },
    {
      field: "deleteRow",
      flex: 1,
      renderCell: (_, index) => (
        <IconButton
          onClick={() => handleDelete(index)}
          color="error"
          aria-label={formatMessage({
            id: "priceList.createCustomItems.column.delete",
            defaultMessage: "Delete Item",
          })}
        >
          <DeleteIcon />
        </IconButton>
      ),
    },
  ];
  return <Table columns={columns} rows={customItems} {...delegateProps} />;
};

const Wrapper = styled("div")({});

type Props = Partial<React.ComponentProps<typeof Table>>;

const AppendableCreateCustomItemsTable: FC<Props> = ({ ...delegateProps }) => {
  const name = "items";
  const { fields, remove, append } = useFieldArray<FormData>({ name });

  return (
    <Wrapper>
      <ItemsTable
        fieldArrayName={name}
        customItems={fields}
        handleDelete={remove}
        {...delegateProps}
      />
      <Button
        color="secondary"
        startIcon={<AddIcon />}
        onClick={() => append({ id: randomId(), name: null })}
        data-testid="priceList.createCustomItemsPage.addRow.button"
      >
        <FormattedMessage
          id="priceList.createCustomItemsPage.addRow.button"
          defaultMessage="Add Custom Item"
        />
      </Button>
    </Wrapper>
  );
};

export default AppendableCreateCustomItemsTable;
